import './style.css';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, writeBatch } from 'firebase/firestore';
import { ref as sref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, signIn, signOut, watchAuth, userEmail, call } from './firebase.js';
import {
  S, emit, onChange, onDelivered, onMessage, unreadCount, startBase, startDay, stopListening, isDispatch, isAdmin,
  driverStops, driverName, officeName, routeKey, dayDrivers, stopPoint, workingDrivers,
} from './store.js';
import { optionsModal, readFields } from './ui/options.js';
import { $, esc, toast, errMsg, chime, lsGet, lsSet, ymd, parseHoursText, normHours, fmtTime, assignRoute, cleanOptions } from './util.js';
import { closeModal, modalOpen } from './ui/common.js';
import * as D from './ui/dispatch.js';
import { mountMap, updateMap, refitMap } from './ui/map.js';
import { openReport, closeReport } from './ui/report.js';
import { helpPage } from './ui/help.js';
import { chatPage, badge, pendingFiles } from './ui/chat.js';
import { enablePush, refreshPush } from './push.js';
import { R, reportsTab, loadReports, lookupCase, csvFor } from './ui/reports.js';
import { driverView, deliverModal, driverAddStopModal, startTracking, stopTracking, tracking, viewingEmail, endRouteModal, initSigPad, clearSigPad, sigSigned, sigBlob } from './ui/driver.js';

const api = {
  syncPilot: call('syncPilot'), optimizeRoutes: call('optimizeRoutes'), refreshRouteEtas: call('refreshRouteEtas'),
  refreshOffice: call('refreshOffice'), resendDeliveryEmail: call('resendDeliveryEmail'), syncOffices: call('syncOffices'), retryOfficeLookups: call('retryOfficeLookups'),
  setRouteDrivers: call('setRouteDrivers'),
};

S.sound = lsGet('wdr-sound') === '1';
S.alerts = lsGet('wdr-alerts') !== '0';
try { S.inclClosed = new Set(JSON.parse(lsGet(`wdr-incl-${S.date}`) || '[]')); } catch { S.inclClosed = new Set(); } // closed offices a driver added back to the directions // on once the browser allows it, until switched off here
S.driverPick = lsGet('wdr-driver') || '';

// ---------------------------------------------------------------- shell
// Line icons in the same 24px, 2px-stroke style as Pilot's sidebar.
const ICON = {
  today: '<path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z"/><path d="M9 4v14M15 6v14"/>',
  plan: '<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>',
  offices: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M10 21v-4h4v4"/>',
  reports: '<path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-6"/>',
  settings: '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M2 14h4M10 8h4M18 16h4"/>',
  driver: '<path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/>',
  chat: '<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01"/>',
  out: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>',
};
const icon = (k) => `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON[k]}</svg>`;
const PAGE = { today: 'Today', plan: 'Plan route', offices: 'Offices', reports: 'Reports', settings: 'Settings' };

function shell() {
  const side = isDispatch();
  const navItem = (k) => `<button class="nav-i" data-tab="${k}" aria-current="${S.view === 'dispatch' && S.tab === k && !S.help && !S.chat}">${icon(k)}${PAGE[k]}</button>`;
  return `<div class="app${side ? ' has-side' : ''}">
  ${side ? `<aside class="side">
    <div class="side-brand"><img src="/watson-logo.png" alt="Watson Dental Laboratory" width="36" height="36"><div><b>Delivery Routes</b><div class="small muted">${esc(S.lab.name)}</div></div></div>
    <nav class="side-nav">${['today', 'plan', 'offices', ...(isAdmin() ? ['reports'] : [])].map(navItem).join('')}${['admin', 'shipping'].includes(S.me?.role) ? navItem('settings') : ''}
      <div class="side-sep"></div>
      <button class="nav-i" data-view="driver" aria-current="${S.view === 'driver' && !S.help && !S.chat}">${icon('driver')}Driver view</button>
      <button class="nav-i" data-act="chat" aria-current="${!!S.chat}">${icon('chat')}Messages <span data-badge="chat"></span></button>
      <button class="nav-i" data-act="help" aria-current="${!!S.help}">${icon('help')}Help</button></nav>
    <div class="side-foot"><div class="small muted" title="${esc(S.email)}">${esc(S.me?.name || S.email)}</div><button class="nav-i" data-act="signOut">${icon('out')}Sign out</button></div>
  </aside>` : ''}
  <div class="wrap">
  <header class="top">
    <div class="brand"><img src="/watson-logo.png" alt="Watson Dental Laboratory" width="36" height="36"><h1>${S.chat ? 'Messages' : S.help ? 'Help' : S.view === 'driver' ? 'My route' : esc(PAGE[S.tab] || 'Today')}</h1></div>
    <div class="row">
      <input type="date" id="datePick" aria-label="Route date" value="${esc(S.date)}">
      ${side ? `<div class="seg" role="group" aria-label="View"><button data-view="dispatch" aria-pressed="${S.view === 'dispatch'}">Dispatch</button><button data-view="driver" aria-pressed="${S.view === 'driver'}">Driver</button></div>` : ''}
      <button class="btn ghost small top-chat" data-act="chat" aria-label="Messages">Messages <span data-badge="chat"></span></button>
      <button class="btn ghost small top-help" data-act="help" aria-label="Help">Help</button>
      <button class="btn ghost small top-out" data-act="signOut" title="${esc(S.email)}">Sign out</button>
    </div>
  </header>
  <div id="offline" class="banner" hidden>You're offline. Drop-offs are saved on this device and sync when you have signal.</div>
  <main id="main"></main></div></div>`;
}

let mounted = null; // which screen skeleton is in #main
let pendingRender = false;
let driverMapEl = null; // kept across re-renders so the driver's map isn't rebuilt every update
function render() {
  if (!S.user || !S.me) return;
  // Dispatch ended this driver's route from the office: stop sharing location on this phone.
  const mineR = S.routes[S.email];
  if (tracking().watch != null && mineR?.finishedAt && !mineR.trackingOn) { stopTracking(false); toast('Your route was ended. Location sharing is off.'); }
  const main = $('#main'); if (!main) return;
  document.querySelectorAll('[data-badge=chat]').forEach((el) => { el.innerHTML = badge(unreadCount()); });
  const key = S.chat ? 'chat' : S.help ? 'help' : S.view === 'driver' ? 'driver' : S.tab;
  if (S.chat) {
    renderChat(main);
  } else if (S.help) {
    if (mounted !== 'help') { main.innerHTML = helpPage(); mounted = 'help'; window.scrollTo(0, 0); }
  } else if (S.view === 'driver') {
    keepScroll(main, () => { main.innerHTML = driverView(); });
    const slot = $('#dmapSlot'); const who = viewingEmail();
    if (slot && who) {
      if (!driverMapEl) { driverMapEl = document.createElement('div'); driverMapEl.className = 'gmap dmap'; }
      slot.replaceWith(driverMapEl);
      mountMap(driverMapEl, { driver: who });
      updateMap();
    }
    mounted = 'driver';
  } else if (S.tab === 'today') {
    if (mounted !== 'today') {
      main.innerHTML = D.tabsHtml() + D.todayShell();
      mountMap($('#mapEl'));
      mounted = 'today';
    }
    const feed = $('#todayAside .feed')?.scrollTop;
    $('#todayAside').innerHTML = D.todayAside();
    if (feed) $('#todayAside .feed').scrollTop = feed;
    $('#legend').innerHTML = D.legendHtml();
    $('#todayTable').innerHTML = D.todayTable();
    updateMap();
  } else {
    // Don't wipe a form someone is typing in.
    if (mounted === key && main.contains(document.activeElement) && /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) { pendingRender = true; return; }
    if (S.tab === 'reports' && !isAdmin()) S.tab = 'today'; // reports are for admins only
    if (S.tab === 'reports') loadReports();
    const html = D.tabsHtml() + ({ plan: D.planTab, offices: D.officesTab, settings: D.settingsTab, reports: reportsTab }[S.tab] || D.planTab)();
    const active = document.activeElement?.id;
    keepScroll(main, () => { main.innerHTML = html; });
    if (active && $(`#${active}`)) { const el = $(`#${active}`); el.focus(); if (el.setSelectionRange && el.type === 'search') el.setSelectionRange(el.value.length, el.value.length); }
    mounted = key;
  }
  const dp = $('#datePick'); if (dp && dp.value !== S.date) dp.value = S.date;
}
document.addEventListener('visibilitychange', () => { if (!document.hidden && S.chat) render(); });
// Tapping a message notification while the app is already open jumps to Messages.
navigator.serviceWorker?.addEventListener?.('message', (e) => { if (e.data?.type === 'openChat' && S.user) { S.chat = true; S.help = false; fullRender(); } });

const urlCache = new Map();
const fileUrl = (path) => { if (!urlCache.has(path)) urlCache.set(path, getDownloadURL(sref(storage, path)).catch((e) => { urlCache.delete(path); throw e; })); return urlCache.get(path); };

// Messages page: keep what's being typed, and only redraw the conversation and list.
let chatKey = null;
function renderChat(main) {
  const { html, read } = chatPage();
  const key = `chat:${S.chatWith || ''}`;
  const log = $('#chatLog'); const atBottom = !log || log.scrollHeight - log.scrollTop - log.clientHeight < 60;
  if (mounted !== 'chat' || chatKey !== key) {
    main.innerHTML = html; mounted = 'chat'; chatKey = key;
  } else {
    const tmp = document.createElement('div'); tmp.innerHTML = html;
    for (const sel of ['#chatLog', '.chat-list', '.chat-pane > .pad']) { const a = main.querySelector(sel); const b = tmp.querySelector(sel); if (a && b && a.innerHTML !== b.innerHTML) a.innerHTML = b.innerHTML; }
  }
  const l2 = $('#chatLog'); if (l2 && atBottom) l2.scrollTop = l2.scrollHeight;
  // Fill in photo previews.
  main.querySelectorAll('img[data-src-path]:not([src])').forEach((img) => {
    fileUrl(img.dataset.srcPath).then((u) => { img.src = u; img.onload = () => { const lg = $('#chatLog'); if (lg && atBottom) lg.scrollTop = lg.scrollHeight; }; }).catch(() => { img.alt = "Photo couldn't load"; });
  });
  // Mark what's on screen as read.
  const now = Date.now(); const upd = {};
  for (const t of read) if (unreadCount(t)) upd[t] = now;
  if (Object.keys(upd).length && !document.hidden) { S.reads = { ...S.reads, ...upd }; setDoc(doc(db, 'reads', S.email), upd, { merge: true }).catch(() => {}); }
}

function keepScroll(_, fn) { const y = window.scrollY; fn(); window.scrollTo(0, y); }

function fullRender() {
  mounted = null;
  $('#app').innerHTML = shell();
  render();
}

onChange(render);
setInterval(() => { if (S.me && !modalOpen()) render(); }, 30000); // refresh "x min ago" labels
window.addEventListener('tracking', () => { if (S.view === 'driver' && !modalOpen()) render(); });
window.addEventListener('online', () => { $('#offline')?.setAttribute('hidden', ''); });
window.addEventListener('offline', () => { $('#offline')?.removeAttribute('hidden'); });

// New message: sound, a toast, and a desktop alert if the tab is in the background.
onMessage((m) => {
  const who = m.fromName || driverName(m.from);
  const where = m.thread === 'all' ? ' (to everyone)' : '';
  const what = m.text || (m.files?.length ? `sent ${m.files.length === 1 ? 'a file' : `${m.files.length} files`}` : '');
  if (!(S.chat && (S.chatWith === m.thread || !isDispatch()))) toast(`Message from ${who}${where}: ${what.slice(0, 80)}`);
  chime();
  try { if (document.hidden && Notification.permission === 'granted' && (!isDispatch() || D.alertsOn())) new Notification(`Message from ${who}`, { body: what.slice(0, 120), icon: '/watson-logo.png' }); } catch { /* optional */ }
});

onDelivered((s) => {
  if (!s || S.view !== 'dispatch' || !isDispatch()) return;
  const msg = `${driverName(s.driverEmail)} ${s.status === 'delivered' ? 'delivered to' : "couldn't deliver to"} ${officeName(s)} at ${fmtTime(s.deliveredAt)}`;
  toast(msg);
  if (S.sound) chime();
  try { if (document.hidden && D.alertsOn()) new Notification('Watson Delivery Routes', { body: msg, icon: '/watson-logo.png' }); } catch { /* optional */ }
});

// ---------------------------------------------------------------- auth
function signInScreen(message = '') {
  $('#app').innerHTML = `<div class="signin"><div class="panel pad stack" style="max-width:420px;margin:12vh auto 0">
    <img src="/watson-logo.png" alt="Watson Dental Laboratory" width="80" height="80"><h1>Watson Delivery Routes</h1>
    <p class="muted">Sign in with your Watson Dental Laboratory Microsoft account.</p>
    ${message ? `<div class="note warn">${esc(message)}</div>` : ''}
    <button class="btn primary big" data-act="signIn">Sign in with Microsoft</button></div></div>`;
}

watchAuth(async (user) => {
  stopListening();
  S.user = user; S.me = null;
  if (!user) { signInScreen(); return; }
  S.email = userEmail(user);
  try {
    const snap = await getDoc(doc(db, 'staff', S.email));
    if (!snap.exists() || !snap.data().active) {
      signInScreen(`${S.email} isn't set up for the routes app yet. Ask an admin to add you under Settings.`);
      return;
    }
    S.me = { email: S.email, ...snap.data() };
  } catch (e) {
    signInScreen(`${S.email} isn't set up for the routes app yet. Ask an admin to add you under Settings.`);
    return;
  }
  S.view = isDispatch() ? (lsGet('wdr-view') || 'dispatch') : 'driver';
  if (location.hash === '#driver') S.view = 'driver';
  if (location.hash === '#chat') S.chat = true; // opened from a message notification
  startBase(); startDay();
  fullRender();
  refreshPush();
  // A driver who reopens the app mid-route picks location sharing back up.
  const r0 = await getDoc(doc(db, 'routes', routeKey(S.email))).catch(() => null);
  if (r0?.exists() && r0.data().startedAt && r0.data().trackingOn && !r0.data().finishedAt) startTracking(S.email);
});

// ---------------------------------------------------------------- actions
async function busy(key, fn) {
  S.busy[key] = true; emit();
  try { return await fn(); } finally { S.busy[key] = false; emit(); }
}

/** Write to Firestore without hanging the UI when offline: the local cache applies it immediately. */
async function save(promise, okMsg) {
  const timeout = new Promise((res) => setTimeout(() => res('queued'), 4000));
  try {
    const r = await Promise.race([promise, timeout]);
    if (r === 'queued') toast('Saved on this device. It will sync when you have signal.');
    else if (okMsg) toast(okMsg);
    return true;
  } catch (e) { toast(errMsg(e)); return false; }
}

async function resizeImage(file) {
  try {
    const img = await createImageBitmap(file);
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement('canvas'); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.8));
  } catch { return file; }
}

function currentPos() {
  const t = tracking().last;
  if (t && Date.now() - t.ts < 120000) return Promise.resolve(t);
  return new Promise((res) => {
    try { navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, ts: Date.now() }), () => res(null), { timeout: 5000, maximumAge: 60000 }); } catch { res(null); }
  });
}

const latLngFrom = (v) => {
  const m = String(v).match(/@?(-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)/) || String(v).match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  return m ? [+m[1], +m[2]] : null;
};

async function setOrder(email, list) {
  const b = writeBatch(db);
  list.forEach((s, i) => { if (s.seq !== i) b.update(doc(db, 'stops', s.id), { seq: i }); });
  await b.commit();
}

const actions = {
  signIn: async () => { try { await signIn(); } catch (e) { signInScreen(errMsg(e)); } },
  signOut: async () => { stopTracking(); await signOut(); },
  closeModal,
  closeModalBg: (el, ev) => { if (ev.target === el) closeModal(); },
  sound: () => { S.sound = !S.sound; lsSet('wdr-sound', S.sound ? '1' : '0'); if (S.sound) chime(); emit(); },
  desktopAlerts: async () => {
    if (typeof Notification === 'undefined') return toast("This browser doesn't support desktop alerts.");
    if (D.alertsOn()) { S.alerts = false; lsSet('wdr-alerts', '0'); toast('Desktop alerts off.'); emit(); return; }
    const p = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    S.alerts = p === 'granted'; lsSet('wdr-alerts', S.alerts ? '1' : '0');
    toast(p === 'granted' ? "Desktop alerts on. You'll get one for each drop-off while this tab is open." : "Chrome is blocking alerts for this site. Click the icon left of the address, then allow Notifications.");
    emit();
  },

  sync: () => busy('sync', async () => {
    try {
      const r = await api.syncPilot({ date: S.date });
      toast(r.cases || r.pickups
        ? `Pilot: ${r.cases} cases${r.tryins ? ` (${r.tryins} try-ins)` : ''}${r.pickups ? ` and ${r.pickups} pickups` : ''} for ${r.offices} offices. ${r.newStops} new stops, ${r.updatedStops} updated.${r.removed ? ` ${r.removed} no longer on Pilot's list for this day, taken off.` : ''}${r.assigned ? ` ${r.assigned} given to their route's driver.` : ''}`
        : 'Pilot has no cases or pickups on your routes for this day yet.');
    } catch (e) { toast(errMsg(e)); }
  }),
  retryLookups: () => busy('retryLookups', async () => {
    try {
      const r = await api.retryOfficeLookups({});
      toast(r.fixed ? `Looked up ${r.fixed} of ${r.tried} offices on Google.` : `Still failing: ${String(r.firstError || '').replace(/^Google lookup failed\.\s*/, '')}`, 10000);
    } catch (e) { toast(errMsg(e)); }
  }),
  syncOffices: () => busy('syncOffices', async () => {
    try {
      const r = await api.syncOffices({});
      const parts = Object.entries(r.byRoute || {}).map(([k, v]) => `${v} ${k}`).join(', ');
      toast(`Pilot: ${r.offices} offices on driver routes (${parts}). ${r.added} new${r.lookedUp ? `; looking up hours for ${r.lookedUp} on Google now` : ''}.`, 9000);
    } catch (e) { toast(errMsg(e)); }
  }),
  routeOpts: (el) => optionsModal(el.dataset.email, { mine: el.dataset.email === S.email && S.view === 'driver' }),
  saveOpts: async (el) => {
    const who = el.dataset.who; const opt = cleanOptions(readFields('o'));
    const asDefault = $('#oSaveDefault')?.checked;
    closeModal();
    const emails = who === '*' ? workingDrivers().map((d) => d.email) : [who];
    const b = writeBatch(db);
    for (const e of emails) b.set(doc(db, 'routes', routeKey(e)), { date: S.date, driverEmail: e, opt }, { merge: true });
    await save(b.commit(), el.dataset.replan === '1' ? null : 'Options saved. Re-plan the route to use them.');
    if (asDefault && who !== '*') await save(updateDoc(doc(db, 'staff', who), { opt }), `Saved as ${who === S.email ? 'your' : `${driverName(who)}'s`} everyday default`);
    if (el.dataset.replan === '1') actions.optimize({ dataset: { email: who === '*' ? '' : who } });
  },
  optimize: (el) => busy('optimize', async () => {
    try {
      const r = await api.optimizeRoutes({ date: S.date, driverEmail: el.dataset.email || null });
      const est = r.drivers.filter((d) => d.optimizer === 'estimate').length;
      const why = r.drivers.find((d) => d.optimizer === 'estimate' && d.optimizerError)?.optimizerError;
      toast(`${r.drivers.length ? 'Routes optimized' : 'No assigned stops to optimize'}${est ? `. Google couldn't optimize, so the order is an estimate.${why ? ` Google said: ${why.replace(/^Route Optimization: /, '')}` : ''}` : '.'}${r.unassigned ? ` ${r.unassigned} stop(s) still need a driver.` : ''}`, est ? 15000 : 6000);
    } catch (e) { toast(errMsg(e)); }
  }),
  refreshEta: async () => { try { await api.refreshRouteEtas({ date: S.date }); toast('ETAs updated'); } catch (e) { toast(errMsg(e)); } },
  resend: async (el) => { el.disabled = true; try { await api.resendDeliveryEmail({ stopId: el.dataset.id }); toast('Email sent'); } catch (e) { toast(errMsg(e)); el.disabled = false; } },
  sigShow: (el) => { $('#sigWrap').hidden = false; el.remove(); const c = $('#sigPad'); c?._fit?.(); },
  sigClear: () => clearSigPad(),
  repView: (el) => { R.view = el.dataset.viewKey; emit(); },
  repCsv: () => {
    const f = csvFor(); if (!f) return;
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([f.text], { type: 'text/csv' })); a.download = f.name;
    document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  },
  podSearch: () => { const q = ($('#podQ')?.value || '').trim(); if (!q) return toast('Enter a case number.'); lookupCase(q); },
  sig: async (el) => {
    const s = findStop(el.dataset.id);
    try { const url = await getDownloadURL(sref(storage, s.signaturePath)); window.open(url, '_blank', 'noopener'); } catch { toast("Couldn't open the signature."); }
  },
  photo: async (el) => {
    const s = findStop(el.dataset.id);
    try { const url = await getDownloadURL(sref(storage, s.photoPath)); window.open(url, '_blank', 'noopener'); } catch { toast("Couldn't open the photo."); }
  },
  undo: (el) => save(updateDoc(doc(db, 'stops', el.dataset.id), { status: 'pending', deliveredAt: null, receivedBy: '', note: '' }), 'Stop reopened'),

  addStop: async () => {
    const officeId = $('#addOffice').value; if (!officeId) return toast('Choose an office first.');
    const o = S.offices[officeId];
    const caseList = $('#addCases').value.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
    const id = `${S.date}_${officeId}_m${Date.now().toString(36)}`;
    const route = o.route || '';
    let driverEmail = $('#addDriver').value || ''; const picked = !!driverEmail;
    if (!picked) { // "Decide later": give it to whoever is driving this office's route today
      const stub = { id, officeId, status: 'pending', driverEmail: '' };
      const same = S.stops.filter((s) => (s.route || S.offices[s.officeId]?.route) === route);
      driverEmail = assignRoute([...same, stub], dayDrivers(route), S.lab, stopPoint).find((c) => c.id === id)?.driverEmail || '';
    }
    await save(setDoc(doc(db, 'stops', id), {
      date: S.date, officeId, officeName: o.name, address: o.address || '', cases: caseList, route,
      driverEmail, assignSource: picked ? 'hand' : 'route', seq: 999, status: 'pending', source: 'manual', createdAt: Date.now(),
    }), `Stop added${driverEmail ? ` for ${driverName(driverEmail)}` : ''}. Optimize to fit it into the route.`);
  },
  splitRoute: (el) => { const r = el.dataset.route; S.splitSlots[r] = Math.min(3, Math.max(dayDrivers(r).length, 1) + 1); emit(); },
  unsplit: (el) => {
    const r = el.dataset.route; const i = Number(el.dataset.slot);
    const list = dayDrivers(r).filter((_, k) => k !== i);
    S.splitSlots[r] = Math.max(1, (S.splitSlots[r] || 2) - 1);
    setRouteDrivers(r, list);
  },
  resetHand: (el) => setRouteDrivers(el.dataset.route, dayDrivers(el.dataset.route), true),
  delStop: (el) => save(deleteDoc(doc(db, 'stops', el.dataset.id)), 'Stop removed'),
  driverAddStop: (el) => driverAddStopModal(el.dataset.email),
  driverSaveStop: async (el) => {
    const email = el.dataset.email;
    const name = $('#daOffice').value.trim().toLowerCase();
    const o = Object.values(S.offices).find((x) => (x.name || '').toLowerCase() === name);
    if (!o) return toast('Pick an office from the list.');
    const where = $('#daWhere').value;
    const mineList = driverStops(email); const open = mineList.filter((s) => s.status === 'pending');
    const seq = where === 'next' && open.length ? (open[0].seq ?? 0) - 0.5 : (mineList.length ? Math.max(...mineList.map((s) => s.seq ?? 0)) + 1 : 0);
    const id = `${S.date}_${o.id}_d${Date.now().toString(36)}`;
    const caseList = $('#daCases').value.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
    const addedNote = $('#daNote').value.trim();
    closeModal();
    await save(setDoc(doc(db, 'stops', id), {
      date: S.date, officeId: o.id, officeName: o.name, address: o.address || '', cases: caseList, route: o.route || '', addedNote,
      driverEmail: email, assignSource: 'hand', seq, status: 'pending', source: 'driver', createdAt: Date.now(),
    }), `${o.name} added to the route`);
    if (where === 'plan') actions.optimize({ dataset: { email } });
    else { const list = driverStops(email); await setOrder(email, list); refreshAfterMove(email); }
  },
  up: (el) => move(el.dataset.id, -1),
  down: (el) => move(el.dataset.id, 1),

  newOffice: () => D.officeModal(null),
  editOffice: (el) => D.officeModal(el.dataset.id),
  useEmail: (el) => { const i = $('#oEmail'); const cur = i.value.split(/[;,]/).map((x) => x.trim()).filter(Boolean); if (!cur.includes(el.dataset.email)) cur.push(el.dataset.email); i.value = cur.join('; '); $('#oNotify').checked = true; el.remove(); },
  googleHours: async (el) => {
    el.disabled = true;
    await updateDoc(doc(db, 'offices', el.dataset.id), { hoursSource: 'google', locationManual: false }).catch(() => {});
    try { const r = await api.refreshOffice({ officeId: el.dataset.id }); toast(r.ok ? 'Updated from Google' : "Google couldn't find this office. Check the name and address."); closeModal(); } catch (e) { toast(errMsg(e)); el.disabled = false; }
  },
  saveOffice: async (el) => {
    const id = el.dataset.id;
    const old = id ? S.offices[id] : {};
    const name = $('#oName').value.trim(); if (!name) return toast('Give the office a name.');
    const address = $('#oAddr').value.trim();
    const patch = {
      name, address, deliveryEmail: $('#oEmail').value.trim(), notifyOnDelivery: $('#oNotify').checked,
      route: $('#oRoute').value,
      phone: $('#oPhone').value.trim(), notes: $('#oNotes').value.trim(),
    };
    if (patch.notifyOnDelivery && !patch.deliveryEmail) return toast('Add an email address, or turn off delivery emails.');
    if (address !== (old.address || '')) patch.addressSource = 'manual';
    if (patch.route !== (old.route || '')) patch.routeSource = 'manual'; // weekly Pilot sync won't overwrite it
    // Hours: only mark as edited when they differ from what was loaded.
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const typed = days.map((k) => $(`#oh_${k}`).value.trim());
    if (typed.some(Boolean)) {
      const hours = {}; days.forEach((k, i) => { hours[k] = parseHoursText(typed[i]); });
      if (JSON.stringify(hours) !== JSON.stringify(normHours(old.hours))) { patch.hours = hours; patch.hoursSource = 'manual'; }
    }
    let lat = parseFloat($('#oLat').value); let lng = parseFloat($('#oLng').value);
    const ll = latLngFrom($('#oLat').value); if (ll) [lat, lng] = ll;
    if (!Number.isNaN(lat) && !Number.isNaN(lng) && (lat !== old.lat || lng !== old.lng)) { patch.lat = lat; patch.lng = lng; patch.locationManual = true; }
    closeModal();
    if (id) await save(updateDoc(doc(db, 'offices', id), patch), 'Office saved');
    else await save(setDoc(doc(collection(db, 'offices')), { ...patch, emailSuggestions: [], createdAt: Date.now(), addressSource: 'manual' }), 'Office added. Looking it up on Google…');
  },
  delOffice: (el) => {
    if (S.confirmDel !== el.dataset.id) { S.confirmDel = el.dataset.id; el.textContent = 'Tap again to delete'; return; }
    closeModal(); save(deleteDoc(doc(db, 'offices', el.dataset.id)), 'Office deleted');
  },

  editStaff: (el) => { if (!isAdmin()) return toast('Only an admin can add or change people.'); D.staffModal(el.dataset.email); },
  saveStaff: async (el) => {
    const email = (el.dataset.email || $('#sEmail').value).trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast('Enter their full email address.');
    const data = { name: $('#sName').value.trim(), role: $('#sRole').value, drives: $('#sDrives').checked, active: $('#sActive').checked,
      opt: $('#sUseLab').checked ? null : cleanOptions(readFields('s')) }; // null: this person uses the lab's defaults
    closeModal();
    await save(setDoc(doc(db, 'staff', email), data, { merge: true }), 'Saved');
  },
  saveLab: async () => {
    const lat = parseFloat($('#labLat').value); const lng = parseFloat($('#labLng').value);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return toast('Enter the lab latitude and longitude.');
    const routeDrivers = {}; document.querySelectorAll('.routeDriver').forEach((s) => { if (s.value) routeDrivers[s.dataset.route] = s.value; });
    await save(setDoc(doc(db, 'config', 'lab'), {
      name: $('#labName').value.trim(), address: $('#labAddr').value.trim(), lat, lng,
      startTime: $('#labStart').value || '08:30', endTime: $('#labEnd').value || '18:00',
      optStyle: $('#labStyle').value === 'farthest' ? 'farthest' : 'normal', returnToLab: $('#labReturn').checked, optFastest: $('#labFastest').checked, optHours: $('#labHours').checked, optAvoidTolls: $('#labTolls').checked, autoOptimize: $('#labAuto').checked, routeDrivers,
      teamsOnMissed: $('#tMissed').checked, teamsOnRouteDone: $('#tDone').checked, teamsOnEachDelivery: $('#tEach').checked,
    }, { merge: true }), 'Settings saved');
  },

  // driver
  startRoute: async () => {
    startTracking(S.email);
    await save(setDoc(doc(db, 'routes', routeKey(S.email)), { date: S.date, driverEmail: S.email, startedAt: Date.now(), trackingOn: true }, { merge: true }), 'Route started. Drive safe!');
  },
  resumeTracking: () => { startTracking(S.email); emit(); },
  finishRoute: async () => {
    stopTracking(false);
    await save(setDoc(doc(db, 'routes', routeKey(S.email)), { date: S.date, driverEmail: S.email, finishedAt: Date.now(), trackingOn: false }, { merge: true }), 'Route ended. Location sharing is off.');
  },
  endRoute: () => { if (!endRouteModal(S.email)) actions.finishRoute(); },
  confirmEndRoute: async () => {
    const skip = $('#erSkip').checked; const why = $('#erReason').value;
    closeModal();
    const now = Date.now();
    if (skip) {
      const b = writeBatch(db);
      driverStops(S.email).filter((s) => s.status === 'pending').forEach((s) => b.update(doc(db, 'stops', s.id), { status: 'skipped', deliveredAt: now, note: `Route ended early${why ? `: ${why}` : ''}` }));
      await save(b.commit(), null);
    }
    await actions.finishRoute();
  },
  restartRoute: async () => {
    startTracking(S.email);
    await save(setDoc(doc(db, 'routes', routeKey(S.email)), { date: S.date, driverEmail: S.email, finishedAt: null, trackingOn: true }, { merge: true }), 'Route restarted');
  },
  refitMap: () => refitMap(),
  help: () => { S.help = true; S.chat = false; fullRender(); },
  chat: () => { S.chat = true; S.help = false; S.chatWith = null; fullRender(); },
  chatOpen: (el) => { S.chatWith = el.dataset.thread; mounted = null; render(); },
  chatSend: (el) => {
    const box = $('#chatText'); const text = (box?.value || '').trim();
    const picked = S.chatFiles || [];
    if (!text && !picked.length) return;
    const thread = el.dataset.thread;
    return busy('chatSend', async () => {
      const ref = doc(collection(db, 'messages'));
      const files = [];
      try {
        // Upload attachments first, then post the message that points to them.
        for (const [i, f] of picked.entries()) {
          const blob = /^image\//.test(f.type) && f.size > 1500000 ? await resizeImage(f) : f;
          const type = blob.type || f.type || 'application/octet-stream';
          const name = f.name.replace(/[^\w.\- ]+/g, '_').slice(-80) || 'file';
          const path = `chat/${thread}/${ref.id}/${i}-${name}`;
          await Promise.race([uploadBytes(sref(storage, path), blob, { contentType: type }), new Promise((_, rej) => setTimeout(() => rej(new Error('slow')), 60000))]);
          files.push({ name: f.name.slice(0, 120), path, type, size: blob.size });
        }
      } catch { toast("The attachment didn't upload (weak signal?). Nothing was sent; try again."); return; }
      box.value = ''; S.chatFiles = []; const pk = $('#chatPicked'); if (pk) pk.innerHTML = '';
      const msg = { thread, from: S.email, fromName: S.me?.name || S.email, text: text.slice(0, 1000), at: Date.now() };
      if (files.length) msg.files = files;
      await save(setDoc(ref, msg), null);
      $('#chatText')?.focus();
    });
  },
  chatUnpick: (el) => { S.chatFiles.splice(Number(el.dataset.i), 1); $('#chatPicked').innerHTML = pendingFiles(); },
  openFile: async (el) => {
    try { window.open(await fileUrl(el.dataset.path), '_blank', 'noopener'); } catch { toast("Couldn't open that file."); }
  },
  closeHelp: () => { S.help = false; fullRender(); },
  closeChat: () => { S.chat = false; fullRender(); },
  enablePush: async (el) => {
    el.disabled = true;
    try { await enablePush(); toast("Notifications are on. You'll get one for each new message."); } catch (e) { toast(e.message || 'Could not turn on notifications.'); }
    mounted = null; render();
  },
  printReport: (el) => openReport(el.dataset.email ? { email: el.dataset.email } : { route: el.dataset.route }),
  printNow: () => window.print(),
  closeReport: () => closeReport(),
  dispatchEndRoute: (el) => {
    const email = el.dataset.email;
    if (S.confirmEnd !== email) { S.confirmEnd = email; el.textContent = 'Tap again to end'; return; }
    S.confirmEnd = null;
    save(setDoc(doc(db, 'routes', routeKey(email)), { date: S.date, driverEmail: email, finishedAt: Date.now(), trackingOn: false, endedBy: S.email }, { merge: true }),
      `Ended ${driverName(email)}'s route. Their open stops can be moved in Plan route.`);
  },
  deliver: (el) => deliverModal(el.dataset.id, false),
  skip: (el) => deliverModal(el.dataset.id, true),
  confirmDeliver: (el) => logStop(el, 'delivered'),
  confirmSkip: (el) => logStop(el, 'skipped'),
};

/** Save who drives a route today; the server moves that route's pending stops to them. */
function setRouteDrivers(route, list, resetHand = false) {
  const before = S.day;
  S.day = { ...S.day, routeDrivers: { ...(S.day.routeDrivers || {}), [route]: list } }; // show it right away
  return busy('rd:' + route, async () => {
    try {
      const r = await api.setRouteDrivers({ date: S.date, route, drivers: list, resetHand });
      const who = list.length ? list.map(driverName).join(' and ') : 'nobody';
      toast(`${route}: ${who}.${r.moved ? ` ${r.moved} stop${r.moved === 1 ? '' : 's'} moved. Optimize to update the order.` : ''}`);
    } catch (e) { S.day = before; toast(errMsg(e)); }
  });
}

async function move(id, dir) {
  const s = S.stops.find((x) => x.id === id); if (!s?.driverEmail) return;
  const list = driverStops(s.driverEmail); const i = list.indexOf(s); const j = i + dir;
  if (j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
  await save(setOrder(s.driverEmail, list));
  refreshAfterMove(s.driverEmail);
}

// After stops are moved by hand on today's started route, re-time the rest with traffic (quietly).
let etaTimer = null;
function refreshAfterMove(email) {
  if (S.date !== ymd() || !S.routes[email]?.startedAt) return;
  clearTimeout(etaTimer);
  etaTimer = setTimeout(() => api.refreshRouteEtas({ date: S.date, driverEmail: email }).catch(() => {}), 3000);
}

// A stop from today's list, the reports' date range, or a proof of delivery search.
const findStop = (id) => S.stops.find((x) => x.id === id) || R.stops.find((x) => x.id === id) || (R.pod.results || []).find((x) => x.id === id);

async function logStop(el, status) {
  const id = el.dataset.id;
  const stop = S.stops.find((x) => x.id === id);
  const signed = status === 'delivered' && sigSigned();
  if (status === 'delivered' && stop?.signatureRequired && (stop.cases || []).length && !signed) { toast('This drop-off needs a signature. Have the office sign on the screen.'); return; }
  if (signed && !($('#dRecv')?.value || '').trim()) { toast("Add the name of the person who signed."); $('#dRecv')?.focus(); return; }
  const sig = signed ? await sigBlob() : null;
  const patch = { status, deliveredAt: Date.now(), note: ($('#dNote')?.value || '').trim() };
  if (status === 'skipped') {
    const why = $('#dReason').value;
    if (!why) { toast('Pick a reason for skipping this stop.'); $('#dReason').focus(); return; }
    if (why === 'Other' && !patch.note) { toast('Add a note saying why.'); $('#dNote').focus(); return; }
    patch.note = [why, patch.note].filter(Boolean).join('. ');
  } else patch.receivedBy = ($('#dRecv')?.value || '').trim();
  el.disabled = true;
  const file = $('#dPhoto')?.files?.[0];
  const pos = await currentPos();
  if (pos) patch.deliveredPos = { lat: pos.lat, lng: pos.lng };
  closeModal();
  if (file) {
    try {
      const blob = await resizeImage(file);
      const path = `photos/${S.date}/${id}/${Date.now()}.jpg`;
      await Promise.race([uploadBytes(sref(storage, path), blob, { contentType: 'image/jpeg' }), new Promise((_, rej) => setTimeout(() => rej(new Error('slow')), 20000))]);
      patch.photoPath = path;
    } catch { toast('The photo didn\'t upload (weak signal?). The drop-off is saved without it.'); }
  }
  if (sig) {
    // Uploaded before the drop-off is saved, so the office email can include it.
    try {
      const path = `photos/${S.date}/${id}/signature-${Date.now()}.png`;
      await Promise.race([uploadBytes(sref(storage, path), sig, { contentType: 'image/png' }), new Promise((_, rej) => setTimeout(() => rej(new Error('slow')), 20000))]);
      patch.signaturePath = path;
    } catch { toast("The signature didn't upload (weak signal?). The drop-off is saved without it."); }
  }
  await save(updateDoc(doc(db, 'stops', id), patch), status === 'delivered' ? 'Drop-off logged' : 'Saved');
}

document.addEventListener('click', (ev) => {
  const t = ev.target.closest('[data-act],[data-tab],[data-view]'); if (!t) return;
  if (t.dataset.tab) { S.help = false; S.chat = false; S.tab = t.dataset.tab; if (S.view !== 'dispatch') { S.view = 'dispatch'; lsSet('wdr-view', S.view); } fullRender(); return; }
  if (t.dataset.view) { S.help = false; S.chat = false; S.view = t.dataset.view; lsSet('wdr-view', S.view); fullRender(); return; }
  const fn = actions[t.dataset.act];
  if (!fn || t.tagName === 'A') return;
  // Only cancel the browser's default for buttons. The pop-up background also carries a data-act, and
  // cancelling there would stop checkboxes, file pickers and expandable sections inside pop-ups from working.
  if (t.tagName === 'BUTTON') ev.preventDefault();
  fn(t, ev);
});
document.addEventListener('change', (ev) => {
  const t = ev.target;
  if (t.id === 'datePick' && t.value) { S.date = t.value; try { S.inclClosed = new Set(JSON.parse(lsGet(`wdr-incl-${S.date}`) || '[]')); } catch { S.inclClosed = new Set(); } startDay(); mounted = null; render(); return; }
  if (t.id === 'pickDriver') { S.driverPick = t.value; lsSet('wdr-driver', t.value); render(); return; }
  if (t.id === 'officeRoute') { S.officeRoute = t.value; mounted = null; render(); return; }
  if (t.id === 'officeSort') { S.officeSort = t.value; mounted = null; render(); return; }
  if (t.id === 'repFrom' || t.id === 'repTo') {
    const f = $('#repFrom').value; const to = $('#repTo').value;
    if (f && to) { R.from = f <= to ? f : to; R.to = f <= to ? to : f; mounted = null; loadReports(); }
    return;
  }
  if (t.id === 'officeActive') { S.officeActive = t.checked; mounted = null; render(); return; }
  if (t.id === 'chatFile') {
    const add = [...t.files]; t.value = '';
    const big = add.filter((f) => f.size > 15 * 1048576);
    if (big.length) toast(`${big.map((f) => f.name).join(', ')} is over 15 MB and can't be sent.`);
    S.chatFiles = [...(S.chatFiles || []), ...add.filter((f) => f.size <= 15 * 1048576)].slice(0, 5);
    if ((S.chatFiles.length) === 5 && add.length > 5) toast('Up to 5 files per message.');
    $('#chatPicked').innerHTML = pendingFiles();
    return;
  }
  if (t.dataset.act === 'inclClosed') {
    S.inclClosed = S.inclClosed || new Set();
    if (t.checked) S.inclClosed.add(t.dataset.id); else S.inclClosed.delete(t.dataset.id);
    lsSet(`wdr-incl-${S.date}`, JSON.stringify([...S.inclClosed]));
    render(); return;
  }
  if (t.dataset.act === 'sigReq') { save(updateDoc(doc(db, 'stops', t.dataset.id), { signatureRequired: t.checked }), t.checked ? 'Signature required for this drop-off' : 'Signature optional'); return; }
  if (t.dataset.act === 'assign') save(updateDoc(doc(db, 'stops', t.dataset.id), { driverEmail: t.value, assignSource: 'hand', seq: 999 }));
  if (t.dataset.act === 'routeDrv') {
    const r = t.dataset.route;
    const vals = [...document.querySelectorAll(`select[data-act=routeDrv][data-route="${CSS.escape(r)}"]`)].map((x) => x.value);
    setRouteDrivers(r, [...new Set(vals.filter(Boolean))]);
  }
});
document.addEventListener('input', (ev) => {
  if (ev.target.id === 'officeFilter') { S.officeFilter = ev.target.value; mounted = null; render(); }
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { closeModal(); closeReport(); }
  if (e.key === 'Enter' && e.target.id === 'podQ') actions.podSearch();
  if (e.key === 'Enter' && !e.shiftKey && e.target.id === 'chatText') { e.preventDefault(); actions.chatSend(e.target.closest('form').querySelector('[data-act=chatSend]')); }
});
// Catch up on changes that arrived while someone was typing.
document.addEventListener('focusout', () => { if (pendingRender) setTimeout(() => { if (!/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '')) { pendingRender = false; render(); } }, 150); });

// Roll over to a new day if the app stays open overnight.
setInterval(() => { const t = ymd(); if (S.me && S.lastToday && S.lastToday !== t && S.date === S.lastToday) { S.date = t; startDay(); } S.lastToday = t; }, 60000);
S.lastToday = ymd();

